import { ReactNode } from 'react'
import {
  Positive,
  Negative,
  InProgress,
  Information,
  WarningStatus,
  Pause,
} from '@/lib/icons'
import type { Application as ProtoApplication } from '@/lib/generated-types'

type Application = Pick<ProtoApplication, 'state' | 'stateMessage' | 'imageDeploymentStatus' | 'deletedAt'>

export function getHealthIcon(status: string): ReactNode {
  switch (status) {
    case 'Healthy':
      return <Positive />
    case 'Degraded':
      return <Negative />
    case 'Progressing':
      return <InProgress />
    case 'Suspended':
      return <Pause size={20} />
    case 'Pending':
      return <InProgress />
    default:
      return <Information />
  }
}

export function getSyncIcon(status: string): ReactNode {
  switch (status) {
    case 'Synced':
      return <Positive />
    case 'OutOfSync':
      return <WarningStatus />
    case 'Pending':
      return <InProgress />
    default:
      return <Information />
  }
}

export function getImageDeploymentIcon(status?: string): ReactNode {
  switch (status) {
    case 'deployed':
    case 'success':
      return <Positive />
    case 'failed':
      return <Negative />
    case 'syncing':
      return <InProgress />
    default:
      return <Information />
  }
}

export function getImageDeploymentText(status?: string): string {
  switch (status) {
    case 'deployed':
    case 'success':
      return 'Deployed to ECR'
    case 'failed':
      return 'Failed'
    case 'syncing':
      return 'Syncing...'
    case 'pending':
      return 'Pending'
    default:
      return 'N/A'
  }
}

export function getApplicationState(application: Application) {
  const { healthStatus, syncStatus } = deriveSyncHealthFromState(application.state)

  return {
    isSuspended: application.state === 'Suspended',
    isDeleting: !!application.deletedAt,
    hasCRDError: application.state === 'CRDCreationFailed',
    isOutOfSync: syncStatus === 'OutOfSync',
    isDegraded: healthStatus === 'Degraded',
    isHealthy: application.state === 'Healthy',
    isSetupComplete: application.state === 'Healthy' || application.state === 'Syncing' || application.state === 'OutOfSync' || application.state === 'Degraded' || application.state === 'Suspended',
  }
}

export function deriveSyncHealthFromState(state?: string) {
  switch (state) {
    case 'Healthy':
      return { syncStatus: 'Synced', healthStatus: 'Healthy' }
    case 'OutOfSync':
      return { syncStatus: 'OutOfSync', healthStatus: 'Healthy' }
    case 'Degraded':
      return { syncStatus: 'OutOfSync', healthStatus: 'Degraded' }
    case 'Suspended':
      return { syncStatus: 'Unknown', healthStatus: 'Suspended' }
    case 'Syncing':
      return { syncStatus: 'Unknown', healthStatus: 'Progressing' }
    case 'ValidatingHelm':
    case 'CreatingCRD':
      return { syncStatus: 'Unknown', healthStatus: 'Progressing' }
    case 'HelmValidationFailed':
    case 'CRDCreationFailed':
      return { syncStatus: 'Unknown', healthStatus: 'Degraded' }
    default:
      return { syncStatus: 'Unknown', healthStatus: 'Unknown' }
  }
}

export function shouldHaveCRD(state?: string) {
  return (
    state === 'Syncing' ||
    state === 'Healthy' ||
    state === 'OutOfSync' ||
    state === 'Degraded' ||
    state === 'Suspended' ||
    state === 'Unknown' ||
    state === 'Deleting'
  )
}
